import { AxiosError } from "axios";
import { computed, ref, Ref } from "vue";
import { FormUtil } from "@/utils/FormUtil";
import { KoyouKeiyakuService } from "@/service/KoyouKeiyakuService";
import { KoyouKeiyakuDetail } from "@/types/KoyouKeiyakuDetail";
import { DialogService } from "@/service/DialogService";
import { useForm } from "@/composable/form/form"
import { useRouter } from 'vue-router'
import { RouterConst } from "@/const/Router"
import { useAuthStore } from "@/store/auth";
import { PermissionConst } from "@/const/Permission";
import { SyainService } from "@/service/SyainService";
import { BaseOptionWithoutCode } from "@/types/Option";

/*
* 新規作成含む雇用契約書の編集に関するcomposable
*/
export function useKoyouKeiyakuEdit() {
  const loading = ref(0)
  const isError = ref(false)
  const isSaving = ref(false)
  const authStore = useAuthStore()
  const router = useRouter()

  // 所属の選択を行えるか
  const canSelectSyozoku: Ref<Boolean> = ref(false);

  // 編集に使用する雇用契約オブジェクト
  const editingKoyouKeiyaku: Ref<KoyouKeiyakuDetail> = ref(new KoyouKeiyakuDetail());

  // 担当営業一覧
  const eigyouList: Ref<BaseOptionWithoutCode[]> = ref([])

  const srv = new KoyouKeiyakuService()

  // 所属選択の権限を持つかチェック
  const checkCanSelectSyozoku = async () => {
    canSelectSyozoku.value = authStore.hasPermission(PermissionConst.KOYOUKEIYAKU_CREATE_FOR_ALL)
  }

  // 保存の実行
  const upsert = async (inputData: KoyouKeiyakuDetail) => {
    loading.value++
    isSaving.value = true

    // 入力エラーのリセット
    useForm().removeAllError(inputData);
    DialogService.nowloading('雇用契約書を保存中...');

    const srv = new KoyouKeiyakuService();
    try {
      const res = await srv.upsert(inputData)
      await DialogService.info({
        text: `雇用契約書を保存しました`
      }, true);
      router.push({ name: RouterConst.KOYOUKEIYAKU_DETAIL, params: { id: res.koyou_keiyaku_id } });
    } catch (e) {
      const err = e as AxiosError<any>;
      FormUtil.setLaravelError(inputData, err);
    } finally {
      isSaving.value = false
      loading.value--
    }
  }
  // 保存の実行
  const agreement = async (koyouKeiyakuId: number, status: number, notes: string) => {
    loading.value++
    isSaving.value = true

    // 入力エラーのリセット
    DialogService.nowloading('本人承認状況を保存中...');

    const srv = new KoyouKeiyakuService();
    try {
      const res = await srv.agreement(koyouKeiyakuId, status, notes)
      await DialogService.info({
        text: `変更しました`
      }, true);
    } catch (e) {
      await DialogService.error({
        text: '予期せぬ事態が発生し保存に失敗しました'
      }, true);
      const err = e as AxiosError<any>;
    } finally {
      isSaving.value = false
      loading.value--
    }
  }

  // 入社タイプ新規作成のための初期値設定（Create.vue）
  const initForCreate = async () => {
    editingKoyouKeiyaku.value.initForCreate();
  }

  // 参照データから新規作成する場合（CreateFrom.vue かつ mode === 'create'の場合に参照データをクローンディープ後に行う）
  const initForCreateFrom = async () => {
    editingKoyouKeiyaku.value.initForCreateFromModeCreate();
  }

  // シートタイプが設定、変更された場合
  const initBySheetType = async (mode: string, lastData?: KoyouKeiyakuDetail | null) => {
    editingKoyouKeiyaku.value.initBySheetType(mode, lastData);
  }

  // 読み込み中か判断する
  const isLoading = computed(() => {
    return !(loading.value === 0 || isError.value)
  })

  // 雇用契約の担当営業に設定できるユーザーを取得
  const getKoyouKeiyakuEigyouUser = async () => {
    eigyouList.value = await srv.getKoyouKeiyakuEigyouUserOptions()
  }

  return {
    editingKoyouKeiyaku,
    isSaving,
    upsert,
    isLoading,
    canSelectSyozoku,
    checkCanSelectSyozoku,
    initForCreate,
    initForCreateFrom,
    initBySheetType,
    agreement,
    getKoyouKeiyakuEigyouUser,
    eigyouList
  }
}